import { useState } from "react";
import { Button, EmptyState, PageHeader, Section } from "@lumika/ui";
import { ChannelTokenPicker } from "./ChannelTokenPicker";
import { WIDGET_URL } from "./config";

// Each panel hosts the *deployed* widget via an `<iframe srcDoc>` rather
// than mounting a local component. The iframe gives each mode its own
// document/browsing context, so the floating launcher's `position: fixed`
// mount stays contained in its panel and the imperative `target` mount in
// the embedded panel doesn't conflict with the floating one. The token is
// chosen at runtime (typed or picked from a dealership channel), so the
// srcDoc is built per-token.

const HOST_STYLE = `
  html, body { margin: 0; height: 100%; }
  body {
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.96),
      rgba(245, 248, 250, 0.94)
    );
    font: 600 14px/1.4 -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  }
`;

function floatingSrcDoc(token: string): string {
  return `<!doctype html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <style>
      ${HOST_STYLE}
      .host-note {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        max-width: 70%;
        padding: 14px 18px;
        color: #3f657d;
        background: rgba(19, 59, 84, 0.06);
        border-radius: 12px;
        text-align: center;
      }
    </style>
  </head>
  <body>
    <div class="host-note">
      Floating launcher mounts to the iframe's bottom-right corner ↘
    </div>
    <script
      async
      src="${WIDGET_URL}"
      data-integration-token="${token}"
    ></script>
  </body>
</html>`;
}

function embeddedSrcDoc(token: string): string {
  return `<!doctype html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <style>
      ${HOST_STYLE}
      #lumika-embed { height: 100%; width: 100%; }
    </style>
  </head>
  <body>
    <div id="lumika-embed"></div>
    <script src="${WIDGET_URL}"></script>
    <script>
      (function () {
        var TOKEN = "${token}";
        var attempts = 0;
        function tryInit() {
          var w = window.LumikaWidget;
          if (w && typeof w.init === "function") {
            w.init({ integrationToken: TOKEN, target: "#lumika-embed" });
            return;
          }
          if (attempts++ < 200) {
            setTimeout(tryInit, 50);
          }
        }
        tryInit();
      })();
    </script>
  </body>
</html>`;
}

function embedSnippet(token: string): string {
  return `<script async src="${WIDGET_URL}" data-integration-token="${token}"></script>`;
}

export function ChannelsPlaygroundWidgetPage() {
  const [widgetToken, setWidgetToken] = useState("");
  // The token actually baked into the iframes — separate from the input so
  // typing doesn't reload the widget on every keystroke.
  const [appliedToken, setAppliedToken] = useState("");

  return (
    <div className="flex flex-col gap-4 p-4 md:gap-6 md:p-6">
      <PageHeader
        eyebrow="Website Widget"
        title="Test the deployed widget channel"
        description="Pick a dealership's website channel (or paste a token) and load the widget. Each panel is an isolated iframe so the floating and embedded modes have independent widget instances."
      />

      <Section title="Channel">
        <div className="flex flex-col gap-4">
          <ChannelTokenPicker
            channelType="web"
            token={widgetToken}
            onTokenChange={setWidgetToken}
            tokenLabel="Widget integration token"
          />
          <div>
            <Button
              onClick={() => setAppliedToken(widgetToken.trim())}
              disabled={!widgetToken.trim()}
            >
              {appliedToken ? "Reload widget" : "Load widget"}
            </Button>
          </div>
        </div>
      </Section>

      {appliedToken ? (
        <>
          <pre className="overflow-x-auto rounded-lg bg-foreground px-4 py-3 text-xs leading-relaxed text-background">
            <code>{embedSnippet(appliedToken)}</code>
          </pre>

          <div className="grid gap-4 md:grid-cols-2 md:gap-6">
            <Section
              title="Floating mode"
              description="Script tag includes data-integration-token, so the widget auto-mounts a floating launcher on the iframe's body."
            >
              <iframe
                key={`floating-${appliedToken}`}
                title="Floating widget host"
                srcDoc={floatingSrcDoc(appliedToken)}
                className="h-[640px] w-full rounded-md border bg-card"
              />
            </Section>

            <Section
              title="Embedded mode"
              description="Script tag loads without the data attribute; the page then calls LumikaWidget.init with a target selector to mount the widget inline."
            >
              <iframe
                key={`embedded-${appliedToken}`}
                title="Embedded widget host"
                srcDoc={embeddedSrcDoc(appliedToken)}
                className="h-[640px] w-full rounded-md border bg-card"
              />
            </Section>
          </div>
        </>
      ) : (
        <EmptyState
          title="No widget loaded"
          description="Enter or select a widget integration token above, then Load widget."
        />
      )}
    </div>
  );
}
